import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import '../styles/avatar-editor.css';
import { BACKEND_URL } from '../config';
const AVATAR_OUTPUT_SIZE = 256;

export type AvatarMode = 'photo' | 'emoji' | 'initials' | 'tarot';

export interface AvatarEditorInitialState {
  mode?: AvatarMode;
  emojis?: string[];
  emojiBgColor?: string;
  emojiBgGradient?: string[] | null;
  initialsBgColor?: string;
  initialsBgGradient?: string[] | null;
}

interface AvatarEditorProps {
  pseudo: string;
  token: string;
  onClose: () => void;
  onSaved: () => void;
  /** Pré-remplit certains champs (utilisé par l'onboarding). */
  initialState?: AvatarEditorInitialState;
  /** Désactive la lecture/écriture de l'avatar existant (utile en onboarding). */
  skipExistingAvatarLoad?: boolean;
}

interface HistoryEntry {
  id: string;
  timestamp: number;
}

const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  {
    label: 'Visages',
    emojis: ['😀','😁','😂','🤣','😃','😄','😅','😆','😉','😊','😎','🤩','😇','🥳','🤠','🤗','🤔','🤭','🫡','😏','😌','😴','🥱','😷','🤓','🧐','😈','👻','💀','🤖','👽','🎃'],
  },
  {
    label: 'Animaux',
    emojis: ['🐱','🦊','🐶','🐻','🐼','🐨','🦁','🐯','🐸','🐵','🦉','🦄','😺','🐝','🐞','🦋','🐙','🦈','🐬','🦜','🦩','🐧','🐢','🐘','🐇','🐿️','🦝','🦔','🐺','🐮','🐷','🐔','🦒','🐊','🦎','🐍','🐠','🐡','🦀','🐚','🦅','🦆','🦢','🐒','🦧'],
  },
  {
    label: 'Cartes & Jeu',
    emojis: ['🎴','♠️','♥️','♦️','♣️','🏆','🥇','🥈','🥉','🎯','🎲','🎰','🎪','🎨','🎭','🎬','🎵','🎶','⭐','🌟','✨','💫','🔥','💥','❤️','💜','💙','💚','💛','🧡','🤍','🖤','💎','🎩'],
  },
  {
    label: 'Nature',
    emojis: ['🌸','🌺','🌻','🌹','🌷','🌼','🍀','🌿','🌴','🌳','🌲','🍁','🍂','🌊','⛰️','🏔️','🌈','☀️','🌙','🌍','🌎','🌏','🌵','🍄'],
  },
  {
    label: 'Nourriture',
    emojis: ['☕','🍵','🧋','🍺','🍷','🥂','🍰','🎂','🍕','🍔','🍟','🌮','🍣','🍩','🍪','🧁','🍫','🍬','🍭','🍓','🍒','🍑','🍌','🍎','🍇','🥝','🍉','🥑'],
  },
];

const AVATAR_COLORS = [
  '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
  '#2196f3', '#03a9f4', '#00bcd4', '#009688',
  '#4caf50', '#8bc34a', '#ff9800', '#ff5722',
  '#795548', '#607d8b', '#f44336', '#1a237e',
  '#004d40', '#bf360c', '#263238', '#37474f',
];

const GRADIENT_PRESETS = [
  ['#667eea', '#764ba2'],
  ['#f093fb', '#f5576c'],
  ['#4facfe', '#00f2fe'],
  ['#43e97b', '#38f9d7'],
  ['#fa709a', '#fee140'],
  ['#a18cd1', '#fbc2eb'],
  ['#fccb90', '#d57eeb'],
  ['#e0c3fc', '#8ec5fc'],
  ['#f5576c', '#ff6b6b'],
  ['#667eea', '#43e97b'],
];

// Tarot card constants
const TAROT_SUITS = [
  { id: 'hearts', label: 'Cœur', symbol: '♥', startCard: 1, count: 14, colors: ['#e74c3c', '#c0392b'] },
  { id: 'spades', label: 'Pique', symbol: '♠', startCard: 15, count: 14, colors: ['#34495e', '#2c3e50'] },
  { id: 'diamonds', label: 'Carreau', symbol: '♦', startCard: 29, count: 14, colors: ['#e67e22', '#d35400'] },
  { id: 'clubs', label: 'Trèfle', symbol: '♣', startCard: 43, count: 14, colors: ['#27ae60', '#1e8449'] },
  { id: 'trumps', label: 'Atouts', symbol: '⚜', startCard: 57, count: 22, colors: ['#8e44ad', '#6c3483'] },
];

const VALUE_LABELS = ['', 'A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'V', 'C', 'D', 'R'];

function isNumberCard(cardNumber: number): boolean {
  if (cardNumber >= 57) return false;
  const value = ((cardNumber - 1) % 14) + 1;
  return value <= 10;
}

function getCardSuitInfo(cardNumber: number) {
  if (cardNumber >= 57) return TAROT_SUITS[4];
  return TAROT_SUITS[Math.floor((cardNumber - 1) / 14)];
}

function getCardValue(cardNumber: number): number {
  if (cardNumber === 78) return 22;
  if (cardNumber >= 57) return cardNumber - 56;
  return ((cardNumber - 1) % 14) + 1;
}

interface CardCropParams {
  cropSize: number;
  centerX: number;
  centerY: number;
  marginX: number;
  allowPanX: boolean;
}

function getCardCropParams(cardNumber: number, imgW: number): CardCropParams {
  const isTrump = cardNumber >= 57 && cardNumber <= 77;
  const isExcuse = cardNumber === 78;

  if (isTrump) {
    const cropSize = Math.round(imgW / 1.9);
    return {
      cropSize,
      centerX: imgW / 2,
      centerY: Math.round(imgW * 0.56) + 12,
      marginX: Math.round(imgW * 0.05),
      allowPanX: true,
    };
  }

  const cropSize = Math.round(imgW / 1.15);
  const startY = isExcuse ? 50 : 20;
  return {
    cropSize,
    centerX: imgW / 2,
    centerY: startY + Math.round(cropSize / 2),
    marginX: 0,
    allowPanX: false,
  };
}

function getInitials(pseudo: string, count: number = 2): string {
  const cleaned = pseudo.replace(/[^a-zA-Z0-9]/g, '');
  if (cleaned.length === 0) return '?';
  if (count === 1) return cleaned[0].toUpperCase();
  const capitals = cleaned.match(/[A-Z]/g);
  if (count === 3) {
    if (capitals && capitals.length >= 3) {
      return (capitals[0] + capitals[1] + capitals[2]).toUpperCase();
    }
    return cleaned.substring(0, Math.min(3, cleaned.length)).toUpperCase();
  }
  if (capitals && capitals.length >= 2) {
    return (capitals[0] + capitals[1]).toUpperCase();
  }
  return cleaned.substring(0, Math.min(2, cleaned.length)).toUpperCase();
}

export default function AvatarEditor({
  pseudo,
  token,
  onClose,
  onSaved,
  initialState,
  skipExistingAvatarLoad,
}: AvatarEditorProps) {
  const [mode, setMode] = useState<AvatarMode>(initialState?.mode ?? 'emoji');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Photo state
  const [photoSrc, setPhotoSrc] = useState<string | null>(null);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const photoCanvasRef = useRef<HTMLCanvasElement>(null);
  const photoImgRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Emoji state
  const [selectedEmojis, setSelectedEmojis] = useState<string[]>(
    initialState?.emojis && initialState.emojis.length > 0 ? initialState.emojis : ['😀'],
  );
  const [emojiBgColor, setEmojiBgColor] = useState(initialState?.emojiBgColor ?? '#3f51b5');
  const [emojiBgGradient, setEmojiBgGradient] = useState<string[] | null>(
    initialState?.emojiBgGradient ?? null,
  );
  const emojiCanvasRef = useRef<HTMLCanvasElement>(null);

  // Initials state
  const [initialsCount, setInitialsCount] = useState(2);
  const [initialsBgColor, setInitialsBgColor] = useState(initialState?.initialsBgColor ?? '#2196f3');
  const [initialsBgGradient, setInitialsBgGradient] = useState<string[] | null>(
    initialState?.initialsBgGradient ?? null,
  );
  const initialsCanvasRef = useRef<HTMLCanvasElement>(null);

  // Tarot state
  const [selectedSuit, setSelectedSuit] = useState(0);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [tarotPanX, setTarotPanX] = useState(0);
  const [tarotFlipped, setTarotFlipped] = useState(false);
  const tarotCanvasRef = useRef<HTMLCanvasElement>(null);
  const tarotImgRef = useRef<HTMLImageElement | null>(null);
  const tarotDragRef = useRef<{ startX: number; panX: number } | null>(null);

  const initials = useMemo(() => getInitials(pseudo, initialsCount), [pseudo, initialsCount]);

  // ---- History ----
  useEffect(() => {
    if (skipExistingAvatarLoad) return;

    fetch(`${BACKEND_URL}/api/avatars/${encodeURIComponent(pseudo)}/history`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setHistory(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch(`${BACKEND_URL}/api/avatars/${encodeURIComponent(pseudo)}/info?t=${Date.now()}`)
      .then(r => r.ok ? r.json() : { hasPhoto: false })
      .then(data => {
        if (data.hasPhoto) {
          setMode('photo');
          setPhotoSrc(`${BACKEND_URL}/api/avatars/${encodeURIComponent(pseudo)}?size=512&t=${Date.now()}`);
        }
      })
      .catch(() => {});
  }, [pseudo, skipExistingAvatarLoad]);

  // ---- Photo handling ----
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoSrc(ev.target?.result as string);
      setCropOffset({ x: 0, y: 0 });
      setCropZoom(1);
      setMode('photo');
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!photoSrc) return;
    const img = new Image();
    if (!photoSrc.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      photoImgRef.current = img;
      renderPhotoCrop();
    };
    img.src = photoSrc;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoSrc]);

  const renderPhotoCrop = useCallback(() => {
    const canvas = photoCanvasRef.current;
    const img = photoImgRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d')!;
    const size = AVATAR_OUTPUT_SIZE;
    canvas.width = size;
    canvas.height = size;
    ctx.clearRect(0, 0, size, size);

    const minDim = Math.min(img.width, img.height);
    const scale = (size / minDim) * cropZoom;
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const drawX = (size - drawW) / 2 + cropOffset.x;
    const drawY = (size - drawH) / 2 + cropOffset.y;

    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    ctx.restore();
  }, [cropOffset, cropZoom]);

  useEffect(() => {
    if (photoSrc && photoImgRef.current) renderPhotoCrop();
  }, [cropOffset, cropZoom, photoSrc, renderPhotoCrop]);

  // Photo drag
  const dragRef = useRef<{ startX: number; startY: number; offsetX: number; offsetY: number } | null>(null);

  const handlePhotoDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragRef.current = {
      startX: clientX,
      startY: clientY,
      offsetX: cropOffset.x,
      offsetY: cropOffset.y,
    };
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!dragRef.current) return;
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      setCropOffset({
        x: dragRef.current.offsetX + (clientX - dragRef.current.startX),
        y: dragRef.current.offsetY + (clientY - dragRef.current.startY),
      });
    };
    const handleEnd = () => { dragRef.current = null; };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleEnd);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, []);

  // Tarot horizontal drag
  const handleTarotDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (selectedCard === null) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    tarotDragRef.current = { startX: clientX, panX: tarotPanX };
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!tarotDragRef.current) return;
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const delta = clientX - tarotDragRef.current.startX;
      setTarotPanX(tarotDragRef.current.panX + delta);
    };
    const handleEnd = () => { tarotDragRef.current = null; };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleEnd);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, []);

  const loadHistoryItem = (entry: HistoryEntry) => {
    const url = `${BACKEND_URL}/api/avatars/${encodeURIComponent(pseudo)}/history/${entry.id}`;
    setPhotoSrc(url);
    setCropOffset({ x: 0, y: 0 });
    setCropZoom(1);
    setMode('photo');
  };

  // ---- Emoji rendering ----
  const renderEmojiAvatar = useCallback(() => {
    const canvas = emojiCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const size = AVATAR_OUTPUT_SIZE;
    canvas.width = size;
    canvas.height = size;

    if (emojiBgGradient) {
      const grad = ctx.createLinearGradient(0, 0, size, size);
      grad.addColorStop(0, emojiBgGradient[0]);
      grad.addColorStop(1, emojiBgGradient[1]);
      ctx.fillStyle = grad;
    } else {
      ctx.fillStyle = emojiBgColor;
    }
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();

    // Réinitialiser fillStyle à une couleur unie avant fillText : sinon
    // les emojis "texte" (♠️ ♥️ ♦️ ♣️ ★ ✨…) et certains rendus bitmap
    // (Safari/iOS) héritent du dégradé et deviennent invisibles puisque
    // leurs pixels se confondent avec le fond.
    ctx.fillStyle = 'white';

    const count = selectedEmojis.length;
    if (count === 1) {
      ctx.font = `${Math.floor(size * 0.55)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(selectedEmojis[0], size / 2, size / 2 + size * 0.03);
    } else if (count === 2) {
      const fontSize = Math.floor(size * 0.38);
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(selectedEmojis[0], size * 0.35, size * 0.38);
      ctx.fillText(selectedEmojis[1], size * 0.65, size * 0.62);
    } else if (count === 3) {
      const fontSize = Math.floor(size * 0.3);
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(selectedEmojis[0], size / 2, size * 0.25);
      ctx.fillText(selectedEmojis[1], size * 0.32, size * 0.65);
      ctx.fillText(selectedEmojis[2], size * 0.68, size * 0.65);
    }
  }, [selectedEmojis, emojiBgColor, emojiBgGradient]);

  useEffect(() => {
    if (mode === 'emoji') renderEmojiAvatar();
  }, [mode, renderEmojiAvatar]);

  // ---- Initials rendering ----
  const renderInitialsAvatar = useCallback(() => {
    const canvas = initialsCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const size = AVATAR_OUTPUT_SIZE;
    canvas.width = size;
    canvas.height = size;

    if (initialsBgGradient) {
      const grad = ctx.createLinearGradient(0, 0, size, size);
      grad.addColorStop(0, initialsBgGradient[0]);
      grad.addColorStop(1, initialsBgGradient[1]);
      ctx.fillStyle = grad;
    } else {
      ctx.fillStyle = initialsBgColor;
    }
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'white';
    const fontSize = initialsCount === 3 ? 0.32 : initialsCount === 1 ? 0.5 : 0.4;
    ctx.font = `600 ${Math.floor(size * fontSize)}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, size / 2, size / 2 + size * 0.02);
  }, [initials, initialsCount, initialsBgColor, initialsBgGradient]);

  useEffect(() => {
    if (mode === 'initials') renderInitialsAvatar();
  }, [mode, renderInitialsAvatar]);

  // ---- Tarot rendering ----
  const renderTarotCard = useCallback(() => {
    if (selectedCard === null) return;
    const canvas = tarotCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const size = AVATAR_OUTPUT_SIZE;
    canvas.width = size;
    canvas.height = size;

    if (isNumberCard(selectedCard)) {
      const suit = getCardSuitInfo(selectedCard);
      const value = getCardValue(selectedCard);
      const label = VALUE_LABELS[value] || String(value);

      const grad = ctx.createLinearGradient(0, 0, size, size);
      grad.addColorStop(0, suit.colors[0]);
      grad.addColorStop(1, suit.colors[1]);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = 'white';
      ctx.font = `bold ${Math.floor(size * 0.38)}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, size / 2, size * 0.38);

      ctx.font = `${Math.floor(size * 0.3)}px sans-serif`;
      ctx.fillText(suit.symbol, size / 2, size * 0.68);
    } else {
      const img = tarotImgRef.current;
      if (!img) return;

      const params = getCardCropParams(selectedCard, img.width);
      const half = params.cropSize / 2;
      const panScale = params.cropSize / size;

      let cx = params.centerX - tarotPanX * panScale;
      let cy = params.centerY;

      if (tarotFlipped) {
        cy = img.height - params.centerY;
        cx = img.width - cx;
      }

      const minCx = params.marginX + half;
      const maxCx = img.width - params.marginX - half;
      cx = Math.max(minCx, Math.min(maxCx, cx));

      ctx.save();
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.clip();

      if (tarotFlipped) {
        ctx.translate(size / 2, size / 2);
        ctx.rotate(Math.PI);
        ctx.translate(-size / 2, -size / 2);
      }

      ctx.drawImage(img, cx - half, cy - half, params.cropSize, params.cropSize, 0, 0, size, size);
      ctx.restore();
    }
  }, [selectedCard, tarotPanX, tarotFlipped]);

  useEffect(() => {
    setTarotPanX(0);
    setTarotFlipped(false);
  }, [selectedCard]);

  useEffect(() => {
    if (mode !== 'tarot' || selectedCard === null) return;
    if (isNumberCard(selectedCard)) {
      renderTarotCard();
    } else {
      if (tarotImgRef.current?.dataset.card === String(selectedCard)) {
        renderTarotCard();
      } else {
        const img = new Image();
        img.dataset.card = String(selectedCard);
        img.onload = () => {
          tarotImgRef.current = img;
          renderTarotCard();
        };
        img.src = `/cards/${selectedCard}.png`;
      }
    }
  }, [mode, selectedCard, tarotPanX, tarotFlipped, renderTarotCard]);

  // ---- Upload ----
  const getCurrentCanvas = (): HTMLCanvasElement | null => {
    switch (mode) {
      case 'photo': return photoCanvasRef.current;
      case 'emoji': return emojiCanvasRef.current;
      case 'initials': return initialsCanvasRef.current;
      case 'tarot': return tarotCanvasRef.current;
      default: return null;
    }
  };

  const handleUpload = async () => {
    const canvas = getCurrentCanvas();
    if (!canvas) return;

    setUploading(true);
    setError(null);

    try {
      let uploadCanvas = canvas;

      if (mode === 'photo' && photoImgRef.current) {
        uploadCanvas = document.createElement('canvas');
        uploadCanvas.width = AVATAR_OUTPUT_SIZE;
        uploadCanvas.height = AVATAR_OUTPUT_SIZE;
        const ctx = uploadCanvas.getContext('2d')!;
        const img = photoImgRef.current;
        const size = AVATAR_OUTPUT_SIZE;
        const minDim = Math.min(img.width, img.height);
        const scale = (size / minDim) * cropZoom;
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        const drawX = (size - drawW) / 2 + cropOffset.x;
        const drawY = (size - drawH) / 2 + cropOffset.y;
        ctx.drawImage(img, drawX, drawY, drawW, drawH);
      } else if (mode === 'tarot' && selectedCard !== null && !isNumberCard(selectedCard) && tarotImgRef.current) {
        uploadCanvas = document.createElement('canvas');
        uploadCanvas.width = AVATAR_OUTPUT_SIZE;
        uploadCanvas.height = AVATAR_OUTPUT_SIZE;
        const ctx = uploadCanvas.getContext('2d')!;
        const img = tarotImgRef.current;
        const params = getCardCropParams(selectedCard, img.width);
        const half = params.cropSize / 2;
        const panScale = params.cropSize / AVATAR_OUTPUT_SIZE;
        let cx = params.centerX - tarotPanX * panScale;
        let cy = params.centerY;
        if (tarotFlipped) {
          cy = img.height - params.centerY;
          cx = img.width - cx;
        }
        cx = Math.max(params.marginX + half, Math.min(img.width - params.marginX - half, cx));
        if (tarotFlipped) {
          ctx.translate(AVATAR_OUTPUT_SIZE / 2, AVATAR_OUTPUT_SIZE / 2);
          ctx.rotate(Math.PI);
          ctx.translate(-AVATAR_OUTPUT_SIZE / 2, -AVATAR_OUTPUT_SIZE / 2);
        }
        ctx.drawImage(img, cx - half, cy - half, params.cropSize, params.cropSize, 0, 0, AVATAR_OUTPUT_SIZE, AVATAR_OUTPUT_SIZE);
      }

      const blob = await new Promise<Blob>((resolve, reject) => {
        uploadCanvas.toBlob(
          b => b ? resolve(b) : reject(new Error('Canvas to blob failed')),
          'image/png',
          0.95,
        );
      });

      const arrayBuffer = await blob.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      const res = await fetch(`${BACKEND_URL}/api/avatars/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo, image: base64, format: 'png', token }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || `Erreur ${res.status}`);
      }

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const toggleEmoji = (emoji: string) => {
    setSelectedEmojis(prev => {
      if (prev.includes(emoji)) {
        return prev.length > 1 ? prev.filter(e => e !== emoji) : prev;
      }
      if (prev.length >= 3) {
        return [...prev.slice(1), emoji];
      }
      return [...prev, emoji];
    });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const currentSuit = TAROT_SUITS[selectedSuit];
  const suitCards = useMemo(() => {
    if (selectedSuit === 4) {
      return [...Array.from({ length: 21 }, (_, i) => 57 + i), 78];
    }
    return Array.from({ length: 14 }, (_, i) => currentSuit.startCard + i);
  }, [selectedSuit, currentSuit.startCard]);

  const canSave = (mode !== 'photo' || !!photoSrc) && (mode !== 'tarot' || selectedCard !== null);

  const modes: { id: AvatarMode; label: string; icon: string }[] = [
    { id: 'photo', label: 'Photo', icon: '📷' },
    { id: 'emoji', label: 'Emoji', icon: '😀' },
    { id: 'initials', label: 'Initiales', icon: '🔤' },
    { id: 'tarot', label: 'Carte', icon: '🎴' },
  ];

  return (
    <div className="avatar-editor-overlay" onClick={handleOverlayClick}>
      <div className="avatar-editor-modal" onClick={e => e.stopPropagation()}>
        <div className="avatar-editor-header">
          <h3>Modifier l'avatar</h3>
          <button className="avatar-editor-close" onClick={onClose}>×</button>
        </div>

        <div className="avatar-editor-tabs">
          {modes.map(m => (
            <button
              key={m.id}
              className={`avatar-editor-tab ${mode === m.id ? 'active' : ''}`}
              onClick={() => setMode(m.id)}
            >
              <span className="avatar-editor-tab-icon">{m.icon}</span>
              <span className="avatar-editor-tab-label">{m.label}</span>
            </button>
          ))}
        </div>

        <div className="avatar-editor-body">
          <div className="avatar-editor-preview-area">
            <div className="avatar-editor-preview">
              {mode === 'photo' && !photoSrc && (
                <div className="avatar-editor-photo-placeholder" onClick={() => fileInputRef.current?.click()}>
                  <span>📷</span>
                  <span>Choisir une photo</span>
                </div>
              )}
              {mode === 'tarot' && selectedCard === null && (
                <div className="avatar-editor-photo-placeholder">
                  <span>🎴</span>
                  <span>Choisissez une carte</span>
                </div>
              )}
              <canvas
                ref={photoCanvasRef}
                className="avatar-editor-canvas"
                style={{ display: mode === 'photo' && photoSrc ? 'block' : 'none' }}
                onMouseDown={handlePhotoDragStart}
                onTouchStart={handlePhotoDragStart}
              />
              <canvas
                ref={emojiCanvasRef}
                className="avatar-editor-canvas"
                style={{ display: mode === 'emoji' ? 'block' : 'none' }}
              />
              <canvas
                ref={initialsCanvasRef}
                className="avatar-editor-canvas"
                style={{ display: mode === 'initials' ? 'block' : 'none' }}
              />
              <canvas
                ref={tarotCanvasRef}
                className="avatar-editor-canvas"
                style={{ display: mode === 'tarot' && selectedCard !== null ? 'block' : 'none' }}
                onMouseDown={handleTarotDragStart}
                onTouchStart={handleTarotDragStart}
              />
            </div>
          </div>

          <div className="avatar-editor-controls">
            {mode === 'photo' && (
              <div className="avatar-editor-photo-controls">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <button
                  className="avatar-editor-btn-secondary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {photoSrc ? 'Changer la photo' : 'Choisir une photo'}
                </button>
                {photoSrc && (
                  <div className="avatar-editor-zoom">
                    <label>Zoom</label>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.05"
                      value={cropZoom}
                      onChange={e => setCropZoom(parseFloat(e.target.value))}
                    />
                  </div>
                )}
                {photoSrc && (
                  <p className="avatar-editor-hint">Glissez l'image pour la repositionner</p>
                )}
                {history.length > 0 && (
                  <div className="avatar-editor-history">
                    <div className="avatar-editor-history-label">Photos précédentes</div>
                    <div className="avatar-editor-history-grid">
                      {history.map(entry => (
                        <button
                          key={entry.id}
                          className="avatar-editor-history-item"
                          onClick={() => loadHistoryItem(entry)}
                          title={new Date(entry.timestamp).toLocaleDateString('fr-FR')}
                        >
                          <img
                            src={`${BACKEND_URL}/api/avatars/${encodeURIComponent(pseudo)}/history/${entry.id}`}
                            alt="Avatar précédent"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {mode === 'emoji' && (
              <div className="avatar-editor-emoji-controls">
                <div className="avatar-editor-selected-emojis">
                  {selectedEmojis.map((e, i) => (
                    <span key={i} className="avatar-editor-selected-emoji" onClick={() => toggleEmoji(e)}>
                      {e} ×
                    </span>
                  ))}
                  <span className="avatar-editor-emoji-count">{selectedEmojis.length}/3</span>
                </div>
                <div className="avatar-editor-emoji-picker">
                  {EMOJI_CATEGORIES.map(cat => (
                    <div key={cat.label} className="avatar-editor-emoji-category">
                      <div className="avatar-editor-emoji-category-label">{cat.label}</div>
                      <div className="avatar-editor-emoji-grid">
                        {cat.emojis.map(e => (
                          <button
                            key={e}
                            className={`avatar-editor-emoji-btn ${selectedEmojis.includes(e) ? 'selected' : ''}`}
                            onClick={() => toggleEmoji(e)}
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <ColorPicker
                  color={emojiBgColor}
                  gradient={emojiBgGradient}
                  onColorChange={c => { setEmojiBgColor(c); setEmojiBgGradient(null); }}
                  onGradientChange={g => setEmojiBgGradient(g)}
                />
              </div>
            )}

            {mode === 'initials' && (
              <div className="avatar-editor-initials-controls">
                <div className="avatar-editor-initials-preview-text">
                  Initiales : <strong>{initials}</strong>
                </div>
                <div className="avatar-editor-initials-count">
                  <span className="avatar-editor-initials-count-label">Nombre de lettres</span>
                  <div className="avatar-editor-initials-count-btns">
                    {[1, 2, 3].map(n => (
                      <button
                        key={n}
                        className={`avatar-editor-initials-count-btn ${initialsCount === n ? 'active' : ''}`}
                        onClick={() => setInitialsCount(n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <ColorPicker
                  color={initialsBgColor}
                  gradient={initialsBgGradient}
                  onColorChange={c => { setInitialsBgColor(c); setInitialsBgGradient(null); }}
                  onGradientChange={g => setInitialsBgGradient(g)}
                />
              </div>
            )}

            {mode === 'tarot' && (
              <div className="avatar-editor-tarot-controls">
                {selectedCard !== null && !isNumberCard(selectedCard) && selectedCard >= 57 && selectedCard <= 77 && (
                  <div className="avatar-editor-tarot-options">
                    <p className="avatar-editor-hint">Glissez horizontalement pour ajuster le cadrage</p>
                    <button
                      className={`avatar-editor-btn-secondary ${tarotFlipped ? 'avatar-editor-btn-active' : ''}`}
                      onClick={() => setTarotFlipped(f => !f)}
                    >
                      🔄 Inverser
                    </button>
                  </div>
                )}
                <div className="avatar-editor-tarot-suits">
                  {TAROT_SUITS.map((suit, i) => (
                    <button
                      key={suit.id}
                      className={`avatar-editor-tarot-suit-btn ${selectedSuit === i ? 'active' : ''}`}
                      onClick={() => { setSelectedSuit(i); setSelectedCard(null); }}
                      style={selectedSuit === i ? { borderColor: suit.colors[0], color: suit.colors[0] } : undefined}
                    >
                      <span className="avatar-editor-tarot-suit-symbol">{suit.symbol}</span>
                      <span className="avatar-editor-tarot-suit-label">{suit.label}</span>
                    </button>
                  ))}
                </div>
                <div className="avatar-editor-tarot-cards">
                  {suitCards.map(num => {
                    const value = getCardValue(num);
                    const isTrump = num >= 57 && num <= 77;
                    const isExcuse = num === 78;
                    const isFace = !isTrump && !isExcuse && value >= 11;
                    const useImage = isFace || isTrump || isExcuse;

                    let label = '';
                    if (isExcuse) label = 'Exc';
                    else if (isTrump) label = String(value);
                    else label = VALUE_LABELS[value] || String(value);

                    return (
                      <button
                        key={num}
                        className={`avatar-editor-tarot-card ${selectedCard === num ? 'selected' : ''}`}
                        onClick={() => setSelectedCard(num)}
                        title={label}
                      >
                        {useImage ? (
                          <img src={`/cards/${num}.png`} alt={label} loading="lazy" />
                        ) : (
                          <span className="avatar-editor-tarot-card-label">
                            <span>{label}</span>
                            <span style={{ color: currentSuit.colors[0] }}>{currentSuit.symbol}</span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="avatar-editor-footer">
          {error && <div className="avatar-editor-error">{error}</div>}
          <div className="avatar-editor-actions">
            <button className="avatar-editor-btn-cancel" onClick={onClose}>
              Annuler
            </button>
            <button
              className="avatar-editor-btn-save"
              onClick={handleUpload}
              disabled={uploading || !canSave}
            >
              {uploading ? 'Enregistrement...' : 'Enregistrer l\'avatar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Color Picker sub-component ----

function ColorPicker({
  color,
  gradient,
  onColorChange,
  onGradientChange,
}: {
  color: string;
  gradient: string[] | null;
  onColorChange: (c: string) => void;
  onGradientChange: (g: string[]) => void;
}) {
  return (
    <div className="avatar-editor-color-section">
      <div className="avatar-editor-color-label">Couleur de fond</div>
      <div className="avatar-editor-color-grid">
        {AVATAR_COLORS.map(c => (
          <button
            key={c}
            className={`avatar-editor-color-btn ${!gradient && color === c ? 'active' : ''}`}
            style={{ background: c }}
            onClick={() => onColorChange(c)}
          />
        ))}
      </div>
      <div className="avatar-editor-color-label" style={{ marginTop: 8 }}>Dégradés</div>
      <div className="avatar-editor-color-grid">
        {GRADIENT_PRESETS.map((g, i) => (
          <button
            key={i}
            className={`avatar-editor-color-btn ${gradient && gradient[0] === g[0] && gradient[1] === g[1] ? 'active' : ''}`}
            style={{ background: `linear-gradient(135deg, ${g[0]}, ${g[1]})` }}
            onClick={() => onGradientChange(g)}
          />
        ))}
      </div>
    </div>
  );
}
